import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";
import { ARTICLES } from "@/content/articles";
import { FadeUp } from "@/components/Reveal";
import { Button } from "@/components/ui/button";

const formatDate = (iso, lang) =>
  new Date(iso + "T12:00:00").toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const Articles = () => {
  const { lang } = useLang();
  useSeo(null, {
    el: {
      path: "/articles",
      title: "Άρθρα ψυχοθεραπείας | Ιωάννης Τσίρος",
      description:
        "Σύντομα κείμενα για την ψυχοθεραπεία: η πρώτη συνεδρία, η online ψυχοθεραπεία, το άγχος και η ένταση — γραμμένα από τον Ιωάννη Τσίρο.",
    },
    en: {
      path: "/articles",
      title: "Psychotherapy Articles | Ioannis Tsiros",
      description:
        "Short pieces about therapy: the first session, online work, anxiety and tension — written by Ioannis Tsiros.",
    },
  });
  const c = {
    el: {
      eyebrow: "ΑΡΘΡΑ",
      h1a: "Κείμενα για τον",
      h1b: "εσωτερικό κόσμο.",
      intro: "Σύντομα κείμενα για ό,τι φέρνει συνήθως τους ανθρώπους στη θεραπεία.",
      read: "Διαβάστε",
      minutes: "λεπτά ανάγνωση",
      ctaTitle: "Ξεκινήστε από εδώ",
      ctaText:
        "Αν ένα από αυτά τα κείμενα σας αναγνώρισε, η πρώτη γνωριμία 15 λεπτών είναι ο πιο απλός επόμενος βήματός σας.",
    },
    en: {
      eyebrow: "ARTICLES",
      h1a: "Notes on the",
      h1b: "inner world.",
      intro: "Short pieces about what usually brings people to therapy.",
      read: "Read",
      minutes: "min read",
      ctaTitle: "Start here",
      ctaText:
        "If one of these pieces recognised you, a free 15-minute introduction is the simplest next step.",
    },
  }[lang];

  return (
    <main data-testid="page-articles">
      <section className="container-x pb-24 pt-32 md:pt-40 lg:pt-44">
        <FadeUp>
          <p className="eyebrow">{c.eyebrow}</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl leading-[1.05] text-sage-900 sm:text-5xl lg:text-6xl">
            {c.h1a} <span className="italic text-clay">{c.h1b}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/80">{c.intro}</p>
        </FadeUp>

        <div className="mt-16 border-t border-sage-600/15 lg:mt-24">
          {ARTICLES.map((a, i) => {
            const item = a[lang];
            return (
              <FadeUp key={a.slug} delay={i * 0.08} className="border-b border-sage-600/15">
                <Link
                  to={`/articles/${a.slug}`}
                  data-testid={`articles-link-${a.slug}`}
                  className="group block py-8 md:py-10"
                >
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    {formatDate(a.date, lang)} · {a[lang].minutes} {c.minutes}
                  </p>
                  <h2 className="mt-2 font-display text-2xl leading-snug text-sage-900 transition-colors duration-200 group-hover:text-clay md:text-3xl">
                    {item.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-foreground/75">
                    {item.excerpt}
                  </p>
                  <p className="mt-4 flex items-center gap-2 text-sm font-medium text-sage-700">
                    {c.read}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </p>
                </Link>
              </FadeUp>
            );
          })}
        </div>

        <FadeUp className="mt-20">
          <div className="grain-card bg-card p-8 md:p-12" data-testid="articles-cta">
            <h2 className="font-display text-2xl text-sage-900 md:text-3xl">{c.ctaTitle}</h2>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-foreground/75">{c.ctaText}</p>
            <Button
              asChild
              data-testid="articles-cta-book"
              className="mt-6 rounded-full bg-sage-600 px-7 text-background hover:bg-sage-700"
            >
              <Link to="/intake">{lang === "el" ? "Κλείστε Ραντεβού" : "Book a Consultation"}</Link>
            </Button>
          </div>
        </FadeUp>
      </section>
    </main>
  );
};

export default Articles;
